import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge, MasteryHeatmap, Panel, ReadinessMeter } from '@usacow/ds';
import { api } from '../lib/api';

type Division = 'bronze' | 'silver' | 'gold' | 'platinum';

const SKILL_LABEL: Record<string, string> = {
  parsing: 'Parsing',
  modeling: 'Modeling',
  algorithm_choice: 'Algorithm choice',
  execution: 'Execution',
  proof: 'Proof',
  implementation: 'Implementation',
  retention: 'Retention',
};

type MasteryData = Awaited<ReturnType<typeof api.getMastery>>;

export function Mastery() {
  const navigate = useNavigate();
  const [data, setData] = useState<MasteryData | null>(null);

  useEffect(() => {
    api
      .getMastery()
      .then(setData)
      .catch(() => setData(null));
  }, []);

  if (!data) return <div className="route-loading">Loading…</div>;
  const division = (data.division as Division) ?? 'silver';

  return (
    <div className="scr">
      <div className="scr-head">
        <div>
          <div className="t-eyebrow">mastery &amp; readiness</div>
          <h1 className="scr-h">Where you stand</h1>
        </div>
        <Badge tone={division} dot>
          {division[0]!.toUpperCase() + division.slice(1)}
        </Badge>
      </div>

      <div className="ms-top">
        <Panel title="Division readiness">
          <ReadinessMeter
            division={division}
            score={data.readiness.score}
            threshold={80}
            style={{ width: '100%' }}
          />
        </Panel>
        <Panel title="Reasoning skills">
          <div className="ms-skills">
            {data.skills.map((s) => {
              const v = s.score / 10;
              const color = !s.measured
                ? 'var(--ink-600)'
                : v < 50
                  ? 'var(--incorrect)'
                  : v < 68
                    ? 'var(--partial)'
                    : 'var(--correct)';
              return (
                <div className="ms-skill" key={s.skill}>
                  <span className="ms-skill__n">{SKILL_LABEL[s.skill] ?? s.skill}</span>
                  <span className="ms-skill__bar">
                    <span style={{ width: `${s.measured ? v : 3}%`, background: color }} />
                  </span>
                  <span className="ms-skill__v">{s.measured ? Math.round(v) : '—'}</span>
                </div>
              );
            })}
          </div>
        </Panel>
      </div>

      <Panel
        title="Topic mastery"
        meta={`${data.heatmap.length} cells · red = retention risk`}
        style={{ marginTop: 16 }}
      >
        {data.heatmap.length > 0 ? (
          <MasteryHeatmap
            cells={data.heatmap}
            columns={Math.min(12, Math.max(4, data.heatmap.length))}
            cellSize={22}
            onCellClick={() => navigate('/app/repair')}
          />
        ) : (
          <p className="dim">No topic data yet. Take the diagnostic and run a few drills.</p>
        )}
      </Panel>
    </div>
  );
}
